// @flow
import _ from 'lodash'
import { getDataSource } from '../../../services/DataSource'
import ErrorCodes from '../../../services/ErrorCodes'
import {
  CREATE,
  GET_BY_ID,
  UPDATE,
  GET_ALL,
  DELETE,
  GET_FOR_APP,
  GET_FOR_FLOW,
  GET_FOR_MAINTAINER,
  GET_BY_NAME,
  ADD_AFFILIATE,
  REMOVE_AFFILIATE
} from '../../action-types'
import {
  SET,
  ADD,
  UPDATE as UPDATE_MUTATION,
  DELETE as DELETE_MUTATION,
  MARK_INITIALIZED
} from '../../mutation-types'

export default {
  [GET_ALL]({ commit, state }, { typename }: { typename: string }): Promise<Array<Object>> {
    if (!_.isEmpty(state.items)) {
      return Promise.resolve(state.items)
    }

    return getDataSource().then(ds =>
      ds
        .getAll(typename)
        .then(items => {
          commit(SET, { items: items })
          commit(MARK_INITIALIZED)
          return items || []
        })
        .catch(err => {
          console.error(err)
          return []
        })
    )
  },
  [GET_BY_ID](
    { commit, getters },
    { typename, id }: { typename: string, id: string }
  ): Promise<?Object> {
    let item = getters.getItemById(id)
    if (!_.isEmpty(item)) {
      return Promise.resolve({ ...item })
    }
    return getDataSource().then(ds =>
      ds.getById(typename, id).then(app => {
        if (app) commit(ADD, { data: app })
        return app || {}
      })
    )
  },
  [UPDATE](
    { state, commit },
    payload: { typename: string, id: string, item: Object, uniqueConstraints: ?Array<string> }
  ): Promise<PostResponse> {
    let { typename, id, item, uniqueConstraints } = payload
    const unfulfilledUniqueConstrains = getUnfulfilledUniqueConstraints(
      state.items,
      item,
      uniqueConstraints
    )
    if (isItemUnique(unfulfilledUniqueConstrains)) {
      return getDataSource().then(ds =>
        ds.updateItem(typename, id, item).then(res => {
          commit(UPDATE_MUTATION, { data: { ...item, version: res.version } })
          return res
        })
      )
    } else {
      return Promise.reject({
        code: ErrorCodes.ITEM_ALREADY_EXISTS,
        constraints: unfulfilledUniqueConstrains
      })
    }
  },
  [CREATE](
    { state, commit },
    {
      typename,
      item,
      uniqueConstraints = []
    }: { typename: string, item: Object, uniqueConstraints: ?Array<string> }
  ): Promise<PostResponse> {
    const unfulfilledUniqueConstrains = getUnfulfilledUniqueConstraints(
      state.items,
      item,
      uniqueConstraints
    )
    if (isItemUnique(unfulfilledUniqueConstrains)) {
      return getDataSource().then(ds =>
        ds.createItem(typename, item).then(res => {
          commit(ADD, { data: { ...item, id: res.id, version: res.version } })
          return res
        })
      )
    } else {
      return Promise.reject({
        code: ErrorCodes.ITEM_ALREADY_EXISTS,
        constraints: unfulfilledUniqueConstrains
      })
    }
  },
  [DELETE]({ commit }, { typename, id }: { typename: string, id: string }): Promise<any> {
    return getDataSource().then(ds =>
      ds.deleteItem(typename, id).then(res => {
        commit(DELETE_MUTATION, { id })
        return res
      })
    )
  },
  [GET_FOR_APP](
    { commit, getters, state },
    { typename, appId }: { typename: string, appId: string }
  ): Promise<Array<Object>> {
    if (!_.isEmpty(state.items)) {
      return Promise.resolve(getters.getItemsForApp(appId))
    }
    return getDataSource().then(ds =>
      ds.getForApp(typename, appId).then(items => {
        items.forEach(i => {
          commit(ADD, { data: i })
        })
        return items || []
      })
    )
  },
  [GET_FOR_FLOW](
    { commit, getters, state, dispatch },
    { typename, flowId }: { typename: string, flowId: string }
  ): Promise<Array<Object>> {
    if (!_.isEmpty(state.items)) {
      return Promise.resolve(getters.getItemsByFlow(flowId))
    }
    // Optimization: It's much faster to filter the full set here than issuing a special query
    return dispatch(GET_ALL, { typename }).then(items => {
      return items.filter(item => _.includes(item.flow, flowId))
    })
  },
  [GET_FOR_MAINTAINER](
    { commit, getters, state },
    { typename, userId }: { typename: string, userId: string }
  ): Promise<Array<Object>> {
    if (!_.isEmpty(state.items)) {
      return Promise.resolve(getters.getItemsByMaintainer(userId))
    }
    return getDataSource().then(ds =>
      ds.getForMaintainer(typename, userId).then(items => {
        items.forEach(i => {
          commit(ADD, { data: i })
        })
        return items || []
      })
    )
  },
  [GET_BY_NAME](
    { commit, getters, state },
    { typename, name }: { typename: string, name: string }
  ): Promise<Array<Object>> {
    if (!_.isEmpty(state.items)) {
      return Promise.resolve(getters.getByName(name))
    }
    return getDataSource().then(ds =>
      ds.getByName(typename, name).then(items => {
        return items || []
      })
    )
  },
  [ADD_AFFILIATE](
    { dispatch },
    payload: { typename: string, id: string, affiliateTypename: string, affiliateId: string }
  ): Promise<Object> {
    let typename = payload.typename
    let groupId = payload.id
    let affiliateTypename = payload.affiliateTypename
    let affiliateId = payload.affiliateId
    return dispatch(GET_BY_ID, { typename: typename, id: groupId })
      .then(item => {
        const affiliationExists = item[affiliateTypename]
          ? item[affiliateTypename].some(a => a === affiliateId)
          : false

        if (!affiliationExists) {
          const updatedItem = { ...item }
          updatedItem[affiliateTypename] = item[affiliateTypename]
            ? [affiliateId, ...item[affiliateTypename]]
            : [affiliateId]

          return dispatch(UPDATE, { typename: typename, id: item.id, item: updatedItem })
        }
      })
      .then(() => ({
        affiliateTypename: affiliateTypename,
        groupId: groupId,
        affiliateId: affiliateId
      }))
  },
  [REMOVE_AFFILIATE](
    { dispatch },
    payload: { typename: string, id: string, affiliateTypename: string, affiliateId: string }
  ): Promise<Object> {
    let typename = payload.typename
    let groupId = payload.id
    let affiliateTypename = payload.affiliateTypename
    let affiliateId = payload.affiliateId
    return dispatch(GET_BY_ID, { typename: typename, id: groupId })
      .then(item => {
        const updatedItem = { ...item }
        updatedItem[affiliateTypename] = item[affiliateTypename].filter(a => a !== affiliateId)

        return dispatch(UPDATE, { typename: typename, id: item.id, item: updatedItem })
      })
      .then(() => ({
        affiliateTypename: affiliateTypename,
        groupId: groupId,
        affiliateId: affiliateId
      }))
  }
}

const isItemUnique = (unfulfilledUniqueConstraints: Array<string>): boolean => {
  return _.isEmpty(unfulfilledUniqueConstraints)
}

// For instance: groups should be globally unique by name
const getUnfulfilledUniqueConstraints = (
  allItems: Array<any>,
  item: Object,
  uniqueConstraints: ?Array<string>
): Array<string> =>
  _.filter(uniqueConstraints || [], constraint =>
    _.find(allItems, i => i.id !== item.id && i[constraint] === item[constraint])
  )
